import { useEffect, useState } from "react";
import { getInboxNotifications, getMessageView } from "../api/notification.api";
import { NotificationMessage } from "../types/notification.types";
import { useProfile } from "../../auth/hooks/useProfile";
import NotificationDrawer from "../components/NotificationDrawer";
import PageContent from "../../../app/layouts/PageContent";
import { notifyNotificationsUpdate } from "../notification.store";

const NotificationInboxPage = () => {
  const { user } = useProfile();

  const [messages, setMessages] = useState<NotificationMessage[]>([]);
  const [selectedMessage, setSelectedMessage] =
    useState<NotificationMessage | null>(null);

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
    });
  };

  useEffect(() => {
    if (!user?.id) return;

    const fetchMessages = async () => {
      const response = await getInboxNotifications({
        page: 1,
        limit: 25,
      });

      if (response.success) {
        setMessages(response.data.rows);
      }
    };

    fetchMessages();
  }, [user]);

  return (
    <PageContent>
      <div className="max-w-6xl mx-auto bg-white rounded-xl shadow border overflow-hidden">

        <div className="flex items-center justify-between px-6 py-4 border-b bg-gray-50">
          <div className="flex items-center gap-3">
            <i className="ri-mail-line text-xl text-indigo-600"></i>
            <h2 className="text-lg font-semibold text-gray-800">
              Inbox
            </h2>
          </div>

          <div className="text-sm text-gray-400">
            {messages.length} messages
          </div>
        </div>

        <div>
          {messages.map((msg) => (
            <div
              key={msg.id}
              onClick={async () => {
                const response = await getMessageView(msg.id);

                if (response.success) {
                  setSelectedMessage(response.data);

                  setMessages((prev) =>
                    prev.map((m) =>
                      m.id === msg.id ? { ...m, read: 1 } : m
                    )
                  );

                  notifyNotificationsUpdate();
                }
              }}
              className={`
                flex items-center justify-between
                px-6 py-4 border-b cursor-pointer transition
                ${msg.read ? "bg-gray-50 text-gray-400" : "hover:bg-indigo-50"}
              `}
            >

              <div className="flex items-center gap-4 min-w-0">

                <div className="w-10 h-10 rounded-lg bg-indigo-500 text-white flex items-center justify-center font-semibold text-sm">
                  {msg.sender.first_name[0]}
                </div>

                <div className="min-w-0">

                  <div className="flex items-center gap-2">

                    <span className={`font-semibold ${msg.read ? "text-gray-400" : "text-gray-800"}`}>
                      {msg.sender.first_name} {msg.sender.last_name}
                    </span>

                    {!msg.read && (
                      <span className="w-2 h-2 bg-indigo-500 rounded-full"></span>
                    )}

                  </div>

                  <div className="text-sm truncate max-w-[520px]">
                    <span className="font-medium">
                      {msg.subject}
                    </span>

                    <span className="ml-2">
                      — {msg.body}
                    </span>
                  </div>

                </div>

              </div>

              <div className="text-sm text-gray-400 whitespace-nowrap">
                {formatDate(msg.created_at)}
              </div>

            </div>
          ))}
        </div>
      </div>

      {selectedMessage && (
        <NotificationDrawer
          message={selectedMessage}
          onClose={() => setSelectedMessage(null)}
          onDelete={(id: number) => {
            setMessages((prev) => prev.filter((m) => m.id !== id));
            setSelectedMessage(null);
          }}

          onToggleRead={(id: number) => {
            setMessages((prev) =>
              prev.map((m) =>
                m.id === id
                  ? { ...m, read: m.read === 1 ? 0 : 1 }
                  : m
              )
            );
          }}
        />
      )}
    </PageContent>
  );
};

export default NotificationInboxPage;